/**
 * Render PDF pages to JPEG object URLs, so a PDF can be previewed and paged
 * through in `PhotoLightbox` like any photo.
 *
 * Why render at all: a desktop browser can show a PDF in an `<iframe>`, but a
 * phone browser mostly offers a download instead, and installers read their
 * plans on a phone.
 *
 * pdf.js is imported lazily, so its ~1 MB only loads once someone looks at a
 * PDF. The legacy build is used on purpose: the modern one needs recent browser
 * APIs that older Android phones lack. `wasmUrl` points at the decoders that
 * `pdfjsWasmPlugin` in vite.config.ts publishes; without them a scanned plan
 * (JPX/JBIG2 images) renders blank.
 */
import type { PDFDocumentProxy } from 'pdfjs-dist';

type PdfJs = typeof import('pdfjs-dist');

let pdfjsPromise: Promise<PdfJs> | null = null;

function loadPdfJs(): Promise<PdfJs> {
  pdfjsPromise ??= (async () => {
    const pdfjs = (await import('pdfjs-dist/legacy/build/pdf.mjs')) as PdfJs;
    const workerUrl = (await import('pdfjs-dist/legacy/build/pdf.worker.min.mjs?url')).default;
    pdfjs.GlobalWorkerOptions.workerSrc = workerUrl;
    return pdfjs;
  })();
  pdfjsPromise.catch(() => { pdfjsPromise = null; });
  return pdfjsPromise;
}

// One open document per URL for the session: the list thumbnail and the viewer
// share it, so opening a PDF after its thumbnail rendered costs no second download.
const documents = new Map<string, Promise<PDFDocumentProxy>>();

function openPdf(url: string): Promise<PDFDocumentProxy> {
  let doc = documents.get(url);
  if (!doc) {
    doc = loadPdfJs().then((pdfjs) =>
      pdfjs.getDocument({ url, wasmUrl: `${import.meta.env.BASE_URL}pdfjs-wasm/` }).promise,
    );
    doc.catch(() => documents.delete(url));
    documents.set(url, doc);
  }
  return doc;
}

export async function pdfPageCount(url: string): Promise<number> {
  return (await openPdf(url)).numPages;
}

/** Longest side, in pixels, of a page rendered for the viewer. Enough to read a plan's labels when zoomed. */
export const VIEWER_PAGE_PX = 2400;

/**
 * Render one page (1-based) so its longest side is `maxPx`, as a JPEG object URL.
 * The caller owns the URL and revokes it.
 */
export async function renderPdfPage(url: string, pageNumber: number, maxPx: number): Promise<string> {
  const doc = await openPdf(url);
  const page = await doc.getPage(pageNumber);
  const base = page.getViewport({ scale: 1 });
  const viewport = page.getViewport({ scale: maxPx / Math.max(base.width, base.height) });

  const canvas = document.createElement('canvas');
  canvas.width = Math.floor(viewport.width);
  canvas.height = Math.floor(viewport.height);
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas niet beschikbaar');
  // A PDF page has no background of its own; JPEG would turn it black.
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  await page.render({ canvas, canvasContext: ctx, viewport }).promise;
  page.cleanup();

  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.85));
  if (!blob) throw new Error('PDF-pagina kon niet worden omgezet');
  return URL.createObjectURL(blob);
}

// Thumbnails are kept for the session, like the documents they came from.
const thumbnails = new Map<string, Promise<string>>();

/** Page 1 as a small JPEG object URL, for the file list. */
export function pdfThumbnail(url: string, maxPx = 160): Promise<string> {
  let thumb = thumbnails.get(url);
  if (!thumb) {
    thumb = renderPdfPage(url, 1, maxPx);
    thumb.catch(() => thumbnails.delete(url));
    thumbnails.set(url, thumb);
  }
  return thumb;
}
